<template>
  <section>
    <form method="post" @submit.prevent="onSend">
      <form-wrapper :validator="$v.form">
        <form-group
          horizontal
          name="is_open_register"
          attribute="attributes.open_register"
          :label="$t('attributes.open_register')"
        >
          <b-switch v-model="form.is_open_register" name="is_open_register" />
        </form-group>

        <b-field horizontal :label="$t('attributes.collaborate_name')">
          <b-input v-model="collaborate_name" disabled expanded />
        </b-field>

        <b-field horizontal :label="$t('attributes.collaborate_activity')">
          <b-input v-model="activity_name" disabled expanded />
        </b-field>

        <form-group
          horizontal
          name="quantity"
          :label="$t('attributes.quantity')"
        >
          <b-input
            v-model.number="form.quantity"
            name="quantity"
            type="number"
            min="0"
            :placeholder="
              $t('message.please_input') + $t('attributes.quantity')
            "
            :open-on-focus="true"
          >
          </b-input>
        </form-group>

        <form-group
          horizontal
          name="child_age"
          :label="$t('attributes.child_age')"
        >
          <b-input
            v-model.number="form.child_age"
            name="child_age"
            type="number"
            min="0"
            :placeholder="
              $t('message.please_input') + $t('attributes.child_age')
            "
            :open-on-focus="true"
          >
          </b-input>
        </form-group>

        <form-group
          horizontal
          name="baby_age"
          :label="$t('attributes.baby_age')"
        >
          <b-input
            v-model.number="form.baby_age"
            name="baby_age"
            type="number"
            min="0"
            :placeholder="
              $t('message.please_input') + $t('attributes.baby_age')
            "
            :open-on-focus="true"
          >
          </b-input>
        </form-group>

        <b-field horizontal :label="$t('attributes.departure_date')">
          <b-input :value="this.formatDate(departure_date)" disabled></b-input>
        </b-field>

        <form-group
          horizontal
          name="registration_range"
          attribute="attributes.registration_range"
          :label="$t('attributes.registration_range')"
        >
          <b-datepicker
            v-model="registration_range"
            name="registration_range"
            :date-formatter="formatDates"
            :max-date="max_date"
            :mobile-native="false"
            :placeholder="
              $t('message.please_input') + $t('attributes.registration_range')
            "
            position="is-top-right"
            icon="calendar-today"
            trap-focus
            range
            @input="onSelectRegistrationRange"
          >
          </b-datepicker>
        </form-group>

        <form-group
          horizontal
          name="signup_deadline"
          :label="$t('attributes.signup_deadline')"
        >
          <b-datepicker
            v-model="signup_deadline"
            name="signup_deadline"
            :date-formatter="formatDate"
            :min-date="deadline_min_date"
            :max-date="deadline_max_date"
            :mobile-native="false"
            :placeholder="
              $t('message.please_input') + $t('attributes.signup_deadline')
            "
            position="is-top-right"
            @input="onSelectSignupDeadline"
          >
          </b-datepicker>
        </form-group>

        <form-group
          horizontal
          name="order_modify_time_limit"
          :label="$t('attributes.order_modify_time_limit')"
        >
          <b-input
            v-model.number="form.order_modify_time_limit"
            name="order_modify_time_limit"
            type="number"
            min="0"
            :placeholder="
              $t('message.please_input') +
                $t('attributes.order_modify_time_limit')
            "
            :open-on-focus="true"
          >
          </b-input>
        </form-group>

        <b-field horizontal>
          <b-button
            :loading="sending"
            type="is-primary"
            native-type="submit"
            :style="{ width: '100%' }"
          >
            {{ $t('attributes.update_data') }}
          </b-button>
        </b-field>
      </form-wrapper>
    </form>
  </section>
</template>

<script>
import {
  required,
  numeric,
  minValue,
  maxValue,
  between
} from 'vuelidate/lib/validators'
import { TRAVEL_GROUPS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'TravelGroupCollaborateForm',

  props: {
    travelGroup: {
      required: true,
      type: Object
    }
  },
  data() {
    const maxDate = this.$dayjs(this.travelGroup.departure_date)
      .subtract(1, 'day')
      .toDate()

    return {
      sending: false,

      code: this.travelGroup.code,
      collaborate_name: this.travelGroup.collaborate_name,
      activity_name: this.travelGroup.activity_name,
      departure_date: new Date(this.travelGroup.departure_date),
      registration_range: this.makeRegistrationRange(this.travelGroup),
      signup_deadline: new Date(this.travelGroup.signup_deadline),

      form: this.extractFormValues(this.travelGroup),

      max_date: maxDate
    }
  },
  computed: {
    deadline_min_date() {
      if (this.form.registration_start_date) {
        return this.form.registration_start_date
      }

      return null
    },
    deadline_max_date() {
      if (this.form.registration_end_date) {
        return this.form.registration_end_date
      }

      return this.max_date
    }
  },
  validations() {
    return {
      form: {
        quantity: { required, numeric, minValue: minValue(0) },
        child_age: { required, numeric, minValue: minValue(0) },
        baby_age: { required, numeric, minValue: minValue(0) },
        order_modify_time_limit: { required, numeric, minValue: minValue(0) },
        registration_start_date: { required },
        registration_end_date: {
          required,
          maxValue: maxValue(this.departure_date)
        },
        signup_deadline: {
          required,
          between: between(
            this.form.registration_start_date,
            this.form.registration_end_date
          )
        }
      }
    }
  },
  watch: {
    travelGroup(newGroup) {
      this.code = newGroup.code
      this.collaborate_name = newGroup.collaborate_name
      this.activity_name = newGroup.activity_name
      this.departure_date = new Date(newGroup.departure_date)
      this.registration_range = this.makeRegistrationRange(newGroup)
      this.signup_deadline = new Date(newGroup.signup_deadline)
      this.form = this.extractFormValues(newGroup)

      this.max_date = this.$dayjs(newGroup.departure_date)
        .subtract(1, 'day')
        .toDate()
    }
  },
  methods: {
    extractFormValues(data) {
      return {
        is_open_register: data.is_open_register,
        quantity: data.quantity,
        child_age: data.child_age,
        baby_age: data.baby_age,
        order_modify_time_limit: data.order_modify_time_limit,
        signup_deadline: new Date(data.signup_deadline),
        registration_start_date: new Date(data.registration_start_date),
        registration_end_date: new Date(data.registration_end_date)
      }
    },
    makeRegistrationRange(data) {
      if (data.registration_start_date && data.registration_end_date) {
        return [
          new Date(data.registration_start_date),
          new Date(data.registration_end_date)
        ]
      } else {
        return [new Date(), new Date()]
      }
    },
    onSelectSignupDeadline() {
      this.form.signup_deadline = this.signup_deadline
    },
    onSelectRegistrationRange() {
      this.form.registration_start_date = this.registration_range[0]
      this.form.registration_end_date = this.registration_range[1]
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      const api = `${TRAVEL_GROUPS_API}/${this.code}/collaborate`
      const form = {
        ...this.form,
        signup_deadline: this.formatDate(this.form.signup_deadline),
        registration_start_date: this.formatDate(
          this.form.registration_start_date
        ),
        registration_end_date: this.formatDate(this.form.registration_end_date)
      }

      this.sending = true
      try {
        const res = await this.axios.put(api, form)

        this.$emit('updated', res.data.data)
        notifyService.success(`${this.$t('attributes.update_succeed')}`)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>
